import { useCallback, useState } from 'react'

// The centre picked on the Centre Operations pages (user, 2026-11-04): ONE
// choice -- a centre or "All centres" -- shared by every page in that menu and
// kept across a hard refresh. Each page used to keep its own key, or none, so
// moving between pages reset the picker.
//
// Only a PICK is saved. A page that cannot show the saved value (a centre this
// user is not mapped to, or "All centres" on a page that needs one centre)
// falls back with the plain setter, which leaves storage alone -- so opening
// Checklists does not turn "All centres" into its first centre everywhere.
export const OPS_CENTRE_KEY = 'oh.centreOps.centre'
// Every page's own ALL_CENTRES constant is this same value.
export const ALL_CENTRES = 'all'

const read = (key) => {
  try {
    return window.localStorage.getItem(key) || ''
  } catch (e) {
    return ''
  }
}

/**
 * Returns [centre, setCentre, pickCentre].
 *   setCentre  -- the page's own fallbacks; not saved.
 *   pickCentre -- the user's choice; saved for every page.
 *
 * `fallback`  -- what the page starts on when nothing usable is saved.
 * `allowAll`  -- false on a page that needs one centre: a saved "All centres"
 *                then starts it on `fallback` instead.
 * `legacyKey` -- the page's own key from before the choice was shared, read
 *                until the first pick so nobody's current centre resets.
 */
export const useOpsCentre = ({
  fallback = '',
  allowAll = true,
  legacyKey
} = {}) => {
  const [centre, setCentre] = useState(() => {
    const saved = read(OPS_CENTRE_KEY) || (legacyKey ? read(legacyKey) : '')
    if (!saved || (saved === ALL_CENTRES && !allowAll)) return fallback
    return saved
  })

  const pickCentre = useCallback((value) => {
    setCentre(value)
    try {
      if (value) window.localStorage.setItem(OPS_CENTRE_KEY, value)
    } catch (e) {
      /* storage blocked: the pick still applies on this page */
    }
  }, [])

  return [centre, setCentre, pickCentre]
}
